/**
 * O código declarado pelo painel do dado (E203.F2, ADR-0146 §4): a linha de um documento na
 * custódia abre o código declarado na origem daquele documento (ou de todos, sem a letra); salvar
 * grava o código (`PUT …/{doc}/codigo`, com as observações como estão), e o servidor reconfere o
 * caso e refaz a prévia. O SHA-256 de custódia é prova e não muda.
 *
 * E203.F4.C (F-E203.F4-07): com fundamento obrigatório, pela rota do painel
 * (`PUT /casos/{id}/dados/codigo/{doc}`), que grava o antes e o depois no histórico do caso.
 */
import { useState } from 'react';

import { Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico_ccb';
import { nomeDoDocumento } from '@/lib/exame_eletronico_peca';
import { salvarCodigoPeloPainel } from '@/lib/revisaoDoDado';

import { Fundamento, fundamentoValido } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.codigo;

function daLinha(pecas: readonly ExameEletronicoCcbData[], linha: string | null) {
  // E211.F2: a linha da custódia é `custodia:<letra>` (a chave da revisão de Integridade e Vínculo);
  // E216.F3: a nova é `custodia:<sha256>`, o arquivo pelo SHA-256 de custódia
  const prefixo = ['documento:', 'custodia:'].find((p) => linha?.startsWith(p));
  const alvo = linha && prefixo ? linha.slice(prefixo.length) : null;
  const daLetra =
    alvo === null
      ? []
      : pecas.filter((p) => p.letra === alvo || p.documento_content_sha256 === alvo);
  return daLetra.length > 0 ? daLetra : pecas;
}

function CodigoDaPeca({ peca, ...props }: PropsDoConteudo & { peca: ExameEletronicoCcbData }) {
  const { client, caseId, acoes, onMudou } = props;
  const inicial = peca.codigo_declarado ?? '';
  const [codigo, setCodigo] = useState(inicial);
  const [fundamento, setFundamento] = useState('');
  const gravacao = useGravacao(
    async () => {
      const novo = codigo.trim() === '' ? null : codigo.trim();
      const pedido = { codigo_declarado: novo, observacoes: peca.observacoes_peca ?? null };
      const doc = peca.source_document_id;
      await salvarCodigoPeloPainel(client, caseId, doc, pedido, fundamento.trim());
      return null;
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  const nome = nomeDoDocumento(peca) ?? peca.original_filename;
  const id = `painel-codigo-${peca.source_document_id}`;
  return (
    <div className="space-y-sc-2" data-testid="painel-codigo-peca">
      <Field label={t.rotulo.replace('{nome}', nome)} htmlFor={id}>
        <Input
          id={id}
          className="font-mono"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          data-testid="painel-codigo-campo"
        />
      </Field>
      <Fundamento id={`${id}-fundamento`} valor={fundamento} onChange={setFundamento} />
      <BotaoSalvar
        estado={gravacao.estado}
        desligado={codigo.trim() === inicial.trim() || !fundamentoValido(fundamento)}
        onSalvar={() => void gravacao.salvar()}
        testId="painel-codigo-salvar"
      />
    </div>
  );
}

export function CodigoNoPainel(props: PropsDoConteudo) {
  const pecas = daLinha(props.mesa.ccbs ?? [], props.pedido.origem.linha);
  return (
    <div className="space-y-sc-3" data-testid="painel-codigo">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      {pecas.length === 0 ? <p className="text-body-sm">{t.semDocumentos}</p> : null}
      {pecas.map((peca) => (
        <CodigoDaPeca key={peca.source_document_id} peca={peca} {...props} />
      ))}
    </div>
  );
}
